import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ReloadIcon from '@mui/icons-material/Replay';
import { Collapse } from '@mui/material';
import numeral from 'numeral';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import { Text } from 'components/DataDisplay';
import Image from 'components/Image';
import {
  Checkbox,
  IconButton,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item } from 'components/Layout';

import { getConversionLocationsForSubObjective } from '../../../utils';
import { DropdownSelect } from '../../DropdownSelect';

import { StatusPill } from './MediaStatusPill';

export const SetupMediaCard = props => {
  const {
    mediaOrder,
    activableMedia,
    logo,
    status,
    dateLabel,
    total,
    mediaAds,
    mediaOrders,
    setMediaOrders,
    handleSaveMediaOrder,
    setAds,
    handleSaveExternalId,
    handleSaveAd,
    isCampaignSetupPending,
    isLeadGen,
    mandatsCampaign,
    objectives,
    skipData,
    selected,
    onSelectedChange
  } = props;

  const { domainConfig } = useApp();
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;

  const geoTargetOptions = useMemo(
    () =>
      (activableMedia?.geoTargets || []).map(
        ({ geo = false, audience = false, isDefault = false }) => {
          const hasGeoOption = geo;
          const hasAudienceOption = audience;
          const hasGeoPlusAudienceOption = hasGeoOption && hasAudienceOption;

          if (hasGeoPlusAudienceOption) {
            return {
              label: 'Géo + audience',
              value: 'geoAndAudience',
              isDefault
            };
          }

          if (hasGeoOption) {
            return {
              label: 'Géographique',
              value: 'geo',
              isDefault
            };
          }

          return {
            label: 'Audience',
            value: 'audience',
            isDefault
          };
        }
      ),
    [activableMedia]
  );

  const canDisplayGeoTargetSelect = geoTargetOptions.length > 0;
  const canSetup = skipData
    ? geoTargetOptions.find(el => el.value === 'geo')
    : true;
  const setupAuto = activableMedia?.canSetUp === true;

  const platformForMediaOrder = objectives.platforms.find(platform =>
    platform?.platforms?.includes(mediaOrder.activableMedia)
  );

  const defaultGeoTargetValue = useMemo(() => {
    const { defaultTargetingTypesByMedia = [] } = domainConfig;
    const hasDefault = defaultTargetingTypesByMedia.find(
      el => el.media === activableMedia?.key
    );

    return (
      hasDefault?.targetingType ??
      geoTargetOptions.find(el => el.isDefault)?.value
    );
  }, [domainConfig, activableMedia, geoTargetOptions]);

  const getGeoTarget = () => {
    if (typeof mediaOrder.geoTarget === 'boolean') {
      if (mediaOrder.geoTarget) {
        return 'geo';
      }

      return geoTargetOptions.find(el => el.value !== 'geo')?.value;
    }

    return defaultGeoTargetValue;
  };

  const objectiveForMediaOrder = platformForMediaOrder?.objectives?.find(
    obj => obj.objectiveKey === mediaOrder.objective
  );

  const conversionLocationsForSubObjective = getConversionLocationsForSubObjective(
    objectiveForMediaOrder,
    mediaOrder.subObjective
  );

  const showConversionLocationSelect =
    conversionLocationsForSubObjective.length > 0;

  const conversionLocationItem = {
    campaignObjectiveLabel: 'Lieu de conversion',
    objectives: conversionLocationsForSubObjective.map(location => ({
      objectiveKey: location.locationKey,
      objectiveLabel: location.locationLabel,
      subObjectives: []
    }))
  };

  const conversionMediaOrderForDropdown = {
    ...mediaOrder,
    objective: mediaOrder.conversionLocation ?? null,
    subObjective: null
  };

  const setObjectiveMediaOrders = updater => {
    const updated =
      typeof updater === 'function' ? updater(mediaOrders) : updater;

    const nextOrders = updated.map(order => {
      if (order.id !== mediaOrder.id) {
        return order;
      }

      const objectiveOrSubObjectiveChanged =
        order.objective !== mediaOrder.objective ||
        order.subObjective !== mediaOrder.subObjective;

      if (objectiveOrSubObjectiveChanged) {
        return { ...order, conversionLocation: null };
      }

      return order;
    });

    setMediaOrders(nextOrders);
    const nextOrder = nextOrders.find(order => order.id === mediaOrder.id);
    if (nextOrder) {
      handleSaveMediaOrder(nextOrder);
    }
  };

  const setConversionLocationMediaOrders = updater => {
    const updated =
      typeof updater === 'function' ? updater(mediaOrders) : updater;

    const nextOrders = updated.map(order => {
      if (order.id !== mediaOrder.id) {
        return order;
      }

      const isConversionLocationSelection = conversionLocationsForSubObjective.some(
        location => location.locationKey === order.objective
      );

      if (isConversionLocationSelection) {
        return {
          ...mediaOrder,
          conversionLocation: order.objective
        };
      }

      return order;
    });

    setMediaOrders(nextOrders);
    const nextOrder = nextOrders.find(order => order.id === mediaOrder.id);
    if (nextOrder) {
      handleSaveMediaOrder(nextOrder);
    }
  };

  const setGeoTargetMediaOrder = value => {
    const nextMediaOrder = {
      ...mediaOrder,
      geoTarget:
        value === undefined ? defaultGeoTargetValue === 'geo' : value === 'geo'
    };

    setMediaOrders(prevMediaOrders =>
      prevMediaOrders.map(order => {
        if (order.id === mediaOrder.id) {
          return nextMediaOrder;
        }
        return order;
      })
    );

    handleSaveMediaOrder(nextMediaOrder);
  };

  const renderGeoTargetTooltip = () => {
    return (
      <Text
        component="span"
        style={{
          whiteSpace: 'pre-line',
          color: themeColors.keep_light
        }}
      >
        {geoTargetOptions.map(({ value }) => {
          switch (value) {
            case 'geo':
              return (
                <Text color="inherit">
                  <strong>Géographique :</strong> utilise des critères géo pour
                  le setup auto.
                </Text>
              );

            case 'geoAndAudience':
              return (
                <Text color="inherit">
                  <strong>Géo + audience :</strong> combine l’audience Ermes et
                  des critères géo.
                </Text>
              );
            case 'audiance':
            default:
              return (
                <Text color="inherit">
                  <strong>Audience :</strong> utilise l’audience Ermes.
                </Text>
              );
          }
        })}
      </Text>
    );
  };

  const marginTooltipText =
    "La marge est une partie du budget média que nous n'enverrons pas à la plateforme média";

  const checkMarginValue = value => {
    const numValue = Number(value);

    return Number.isFinite(numValue) && numValue < 100 && numValue >= 0;
  };

  const allMandats = Array.isArray(mandatsCampaign) ? mandatsCampaign : [];

  const mediaKey = activableMedia?.key;

  const shouldDisableMarginRate =
    Boolean(activableMedia?.enableMarginRate) &&
    Boolean(mediaKey) &&
    allMandats.some(
      mediaMandat =>
        mediaMandat?.media === mediaKey && mediaMandat?.mandat === false
    );

  const isDv360Setup = activableMedia?.model === 'dv360';
  const forcePublicInventoryDisabled =
    domainConfig?.features?.enableDeals === true &&
    isDv360Setup &&
    mediaOrder.activableMedia !== 'dv360';

  const hasParams =
    canDisplayGeoTargetSelect ||
    Boolean(platformForMediaOrder) ||
    Boolean(activableMedia?.enableMarginRate);
  const hasCreaParams = (isLeadGen || isDv360Setup) && mediaAds.length > 0;
  const hasBody = hasParams || hasCreaParams;

  const [isExpanded, setIsExpanded] = useState(setupAuto);

  useEffect(() => {
    if (
      !canDisplayGeoTargetSelect ||
      typeof mediaOrder.geoTarget === 'boolean' ||
      !defaultGeoTargetValue
    ) {
      return;
    }

    const nextMediaOrder = {
      ...mediaOrder,
      geoTarget: defaultGeoTargetValue === 'geo'
    };

    setMediaOrders(prevMediaOrders =>
      prevMediaOrders.map(order => {
        if (order.id === mediaOrder.id) {
          return nextMediaOrder;
        }
        return order;
      })
    );

    handleSaveMediaOrder(nextMediaOrder);
  }, [
    canDisplayGeoTargetSelect,
    defaultGeoTargetValue,
    handleSaveMediaOrder,
    mediaOrder,
    setMediaOrders
  ]);

  const fieldLabel = label => (
    <Text
      fontSize="11px"
      fontWeight={600}
      style={{ color: designSystem.inkSoft }}
    >
      {label}
    </Text>
  );

  const renderInventoryToggle = ad => {
    const hasDeals = Array.isArray(ad?.dealIds) && ad.dealIds.length > 0;
    return (
      <Item flex style={{ alignItems: 'center', gap: '7px' }}>
        <Text
          component="span"
          fontSize="11.5px"
          noWrap
          style={{ color: designSystem.muted }}
        >
          Inventory public DV360
        </Text>
        <Switch
          disabled={
            isCampaignSetupPending || !hasDeals || forcePublicInventoryDisabled
          }
          checked={!forcePublicInventoryDisabled && (ad.isChecked || !hasDeals)}
          onChange={() => {
            setAds(prevAds =>
              prevAds.map(crea => {
                if (crea.id === ad.id) {
                  return {
                    ...crea,
                    publicInventory: !ad.isChecked,
                    isChecked: !ad.isChecked
                  };
                }
                return crea;
              })
            );
            handleSaveAd(ad.id, {
              ...ad,
              publicInventory: !ad.isChecked
            });
          }}
          options={['Non', 'Oui']}
        />
      </Item>
    );
  };

  return (
    <Container
      flex
      style={{
        width: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        border: `1px solid ${designSystem.line}`,
        borderRadius: '9px',
        overflow: 'hidden',
        marginBottom: '10px'
      }}
    >
      <Item
        flex
        onClick={hasBody ? () => setIsExpanded(prev => !prev) : undefined}
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '10px 12px',
          backgroundColor: designSystem.bgSoft,
          cursor: hasBody ? 'pointer' : 'default',
          ...(isExpanded && hasBody
            ? { borderBottom: `1px solid ${designSystem.line}` }
            : {})
        }}
      >
        <Item
          flex
          style={{
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'flex-start'
          }}
        >
          <Item
            flex
            onClick={event => event.stopPropagation()}
            style={{ alignItems: 'center' }}
          >
            <Checkbox
              checked={selected}
              onChange={onSelectedChange}
              color={designSystem.green}
              disabled={!setupAuto || !canSetup || isCampaignSetupPending}
            />
          </Item>
          <Image src={logo} alt={mediaOrder.activableMedia} height="28px" />
          <Text component="span" fontSize="13px" fontWeight={700}>
            {activableMedia?.name || mediaOrder.activableMedia}
          </Text>
          <StatusPill status={status} />
        </Item>
        <Item
          flex
          style={{
            alignItems: 'center',
            gap: '12px',
            justifyContent: 'flex-end'
          }}
        >
          {dateLabel && (
            <Item flex style={{ alignItems: 'center', gap: '5px' }}>
              <AccessTimeIcon
                style={{ fontSize: '13px', color: designSystem.muted }}
              />
              <Text
                component="span"
                fontSize="11.5px"
                noWrap
                style={{ color: designSystem.muted }}
              >
                {dateLabel}
              </Text>
            </Item>
          )}
          <Text
            component="span"
            fontSize="13px"
            fontWeight={700}
            style={{ color: designSystem.ink }}
          >
            {numeral(total).format('0,0 $')}
          </Text>
          {hasBody &&
            (isExpanded ? (
              <KeyboardArrowDownIcon style={{ fill: designSystem.faint }} />
            ) : (
              <KeyboardArrowRight style={{ fill: designSystem.faint }} />
            ))}
        </Item>
      </Item>
      <Collapse in={isExpanded && hasBody} timeout="auto" unmountOnExit>
        <Item
          flex
          style={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '12px',
            padding: '12px'
          }}
        >
          {hasParams && (
            <Item
              flex
              style={{
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '14px'
              }}
            >
              {canDisplayGeoTargetSelect && (
                <Item flex style={{ alignItems: 'flex-end', gap: '2px' }}>
                  <Select
                    value={getGeoTarget()}
                    disabled={skipData}
                    title="Ciblage"
                    options={geoTargetOptions}
                    tooltip={renderGeoTargetTooltip()}
                    onChange={value => {
                      setGeoTargetMediaOrder(value);
                    }}
                    width="150px"
                    small
                  />
                  {!skipData && (
                    <IconButton
                      onClick={() => {
                        setGeoTargetMediaOrder(undefined);
                      }}
                    >
                      <ReloadIcon
                        style={{
                          fontSize: '18px',
                          color: designSystem.faint
                        }}
                      />
                    </IconButton>
                  )}
                </Item>
              )}
              {platformForMediaOrder && (
                <Item
                  flex
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '9px',
                    paddingTop: '10px'
                  }}
                >
                  {fieldLabel('Objectif')}
                  <DropdownSelect
                    item={platformForMediaOrder}
                    themeColors={themeColors}
                    setMediaOrders={setObjectiveMediaOrders}
                    mediaOrder={mediaOrder}
                  />
                </Item>
              )}
              {showConversionLocationSelect && (
                <Item
                  flex
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '9px',
                    paddingTop: '10px'
                  }}
                >
                  {fieldLabel('Lieu de conversion')}
                  <DropdownSelect
                    item={conversionLocationItem}
                    themeColors={themeColors}
                    setMediaOrders={setConversionLocationMediaOrders}
                    mediaOrder={conversionMediaOrderForDropdown}
                  />
                </Item>
              )}
              {(activableMedia?.enableMarginRate ?? false) && (
                <Item flex style={{ maxWidth: '150px' }}>
                  <TextField
                    title="Taux de marge"
                    unit="%"
                    small
                    value={Number((mediaOrder?.marginRate * 100).toFixed(3))}
                    tooltip={
                      <Text
                        component="span"
                        style={{
                          whiteSpace: 'pre-line',
                          color: themeColors.keep_light
                        }}
                      >
                        {marginTooltipText}
                      </Text>
                    }
                    disabled={shouldDisableMarginRate}
                    onChange={inputValue => {
                      const numValue = parseFloat(inputValue);
                      if (checkMarginValue(numValue)) {
                        const roundedValue = Math.round(numValue * 1000) / 1000;
                        setMediaOrders(prevMediaOrders =>
                          prevMediaOrders.map(order => {
                            if (order.id === mediaOrder.id) {
                              return {
                                ...order,
                                marginRate: roundedValue / 100
                              };
                            }
                            return order;
                          })
                        );
                      }
                    }}
                    onBlur={() => {
                      handleSaveMediaOrder(mediaOrder);
                    }}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 99,
                      step: 1
                    }}
                  />
                </Item>
              )}
            </Item>
          )}
          {hasCreaParams && (
            <Item
              flex
              style={{ flexDirection: 'column', alignItems: 'stretch' }}
            >
              <Text
                fontSize="10.5px"
                style={{ color: designSystem.muted, marginBottom: '6px' }}
              >
                Créas — paramétrage
              </Text>
              {mediaAds.map(ad => (
                <Item
                  key={ad.id}
                  flex
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    padding: '7px 6px 7px 12px'
                  }}
                >
                  <Text
                    component="span"
                    fontSize="12.5px"
                    style={{ color: designSystem.inkSoft }}
                  >
                    {ad.availableCreative?.name || 'Asset introuvable'}
                  </Text>
                  <Item
                    flex
                    style={{
                      alignItems: 'center',
                      gap: '12px',
                      justifyContent: 'flex-end'
                    }}
                  >
                    {isDv360Setup && renderInventoryToggle(ad)}
                    {isLeadGen && (
                      <Item flex style={{ alignItems: 'center', gap: '7px' }}>
                        <Text
                          component="span"
                          fontSize="10.5px"
                          noWrap
                          style={{ color: designSystem.muted }}
                        >
                          ID de formulaire
                        </Text>
                        <Item flex style={{ maxWidth: '170px' }}>
                          <TextField
                            small
                            placeholder="ex. form_12345"
                            value={ad.leadGenFormId}
                            disabled={isCampaignSetupPending}
                            onChange={inputValue => {
                              setAds(prevAds =>
                                prevAds.map(crea => {
                                  if (crea.id === ad.id) {
                                    return {
                                      ...crea,
                                      leadGenFormId: inputValue
                                    };
                                  }
                                  return crea;
                                })
                              );
                            }}
                            onBlur={inputValue => {
                              handleSaveExternalId(
                                ad.id,
                                ad.externalId,
                                inputValue
                              );
                            }}
                          />
                        </Item>
                      </Item>
                    )}
                  </Item>
                </Item>
              ))}
            </Item>
          )}
        </Item>
      </Collapse>
    </Container>
  );
};

SetupMediaCard.defaultProps = {
  logo: '',
  dateLabel: null,
  isCampaignSetupPending: false,
  isLeadGen: false,
  skipData: false,
  mandatsCampaign: []
};

SetupMediaCard.propTypes = {
  mediaOrder: PropTypes.shape().isRequired,
  activableMedia: PropTypes.shape().isRequired,
  logo: PropTypes.string,
  status: PropTypes.shape().isRequired,
  dateLabel: PropTypes.string,
  total: PropTypes.number.isRequired,
  mediaAds: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  mediaOrders: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setMediaOrders: PropTypes.func.isRequired,
  handleSaveMediaOrder: PropTypes.func.isRequired,
  setAds: PropTypes.func.isRequired,
  handleSaveExternalId: PropTypes.func.isRequired,
  handleSaveAd: PropTypes.func.isRequired,
  isCampaignSetupPending: PropTypes.bool,
  isLeadGen: PropTypes.bool,
  mandatsCampaign: PropTypes.arrayOf(PropTypes.shape()),
  objectives: PropTypes.shape().isRequired,
  skipData: PropTypes.bool,
  selected: PropTypes.bool.isRequired,
  onSelectedChange: PropTypes.func.isRequired
};

export default SetupMediaCard;
